/** Team Members form Owners
 *
 *
 *  For profile pic
 *  @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/file
 *  @see https://docs.strapi.io/dev-docs/plugins/upload
 */
import { capitalizeAndSplit } from '@unionco/alaris-utils'
import { Accordion, Card, FlyoutContext } from '@unionco/components'
import { AxiosResponse } from 'axios'
import { useContext, useEffect, useRef, useState } from 'react'
import tw from 'twin.macro'

import {
  IBasicTeamFormData,
  IContractorFormData,
  IDepartmentHeadFormData,
  IInputLabelValue,
  ILeaderFormData,
  INonOwnerFormData,
  IOwnerFormData,
  IOwnerQuestionData,
  IQuestionSegmentData,
  IQuestionnaireData,
  ITeamMemberFormData,
  TFormAnswerData,
  TFormQuestionData,
  TFormQuestionTypes
} from '@unionco/alaris-app-types'

import { createBooleanArrayFromNumber } from '@unionco/alaris-utils'
import { cx } from '@unionco/utils'
import { getCurrentQuestionData } from 'components/questionnaire/util'
import { getCurrentSegmentData } from 'utils'
import { generateFormDefaults, getFormValues } from './utils'

import { CollapsibleItemsCard, LoadingSpinner, SidebarContext, StyledButton } from 'components'

import { QuestionContext } from '../context'
import BasicTeamForm from './BasicTeamForm'
import ContractorForm from './ContractorForm'
import DepartmentHeadForm from './DepartmentHeadForm'
import LeaderForm from './LeaderForm'
import NonOwnerForm from './NonOwnerForm'
import OwnerForm from './OwnerForm'
import TeamMemberForm from './TeamMemberForm'
import { set } from 'cypress/types/lodash'

export const inputStyling = {
  input: 'u-rounded u-p-400 u-bg-primary-300'
}

type TOwnersForm = {
  className?: string
  data: TFormQuestionData
  questionType: TFormQuestionTypes
}

export const TeamMembersQuestion: React.FC<TOwnersForm> = ({
  className,
  data,
  questionType
}) => {
  // Prop destructing
  const { answers, entriesTitle, triggerQuestionType } = data
  // Context Data
  const { data: stepData, pageLocations } = useContext(SidebarContext)
  const { submitAnswer } = useContext(QuestionContext)
  const { addFlyout } = useContext(FlyoutContext)
  // Local State
  const [savedEntries, setSavedEntries] = useState<boolean[]>([false])
  const [isLoadingSave, setIsLoadingSave] = useState<boolean>(false)
  const [saveResultText, setSaveResultText] = useState<string>('');
  const [entriesData, setEntriesData] = useState<TFormAnswerData[]>(answers)
  // Local Refs
  const questionRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const defaultValue = createBooleanArrayFromNumber(data.answers.length)
    setSavedEntries(defaultValue)
  }, [data.answers.length, setSavedEntries])

  /**
   * Save Entry function
   * Saves the entry to the Strapi API
   * @param index - the index of the entry to be saved
   * @param transactionGoals - the transactionGoals array of objects
   */
  async function saveEntry(index: number, transactionGoals: IInputLabelValue[] | null = null): Promise<null | true | string[]> {
    if (!questionRef || !questionRef.current) return null
    // set loading state
    setIsLoadingSave(true)
    const { current: form } = questionRef
    const entry = form.getElementsByClassName('b-Entry')[index]
    const newState = { ...stepData } as IQuestionnaireData
    const currentSegment = getCurrentSegmentData(
      pageLocations,
      newState
    ) as IQuestionSegmentData

    const currentQuestion = getCurrentQuestionData(
      pageLocations,
      newState
    ) as TFormQuestionData

    const requiredFields = [...entry.querySelectorAll('[required]')] as Array<
      HTMLInputElement | HTMLSelectElement
    >
    const missingFields = requiredFields.filter(
      (f) => f.value === 'unanswered' || f.value === ''
    )
    const valid = missingFields.length === 0
    if (valid) {
      currentQuestion.answers[index] = getFormValues(
        entry,
        questionType,
        answers
      )

      // Sets transactionGoal to the transactionGoals array of objects if it exists
      if ('transactionGoal' in currentQuestion.answers[index]) {
        (currentQuestion.answers[index] as IOwnerFormData).transactionGoal = transactionGoals
      }

      newState.segments[pageLocations['segment']] = currentSegment

      currentQuestion.answers = currentQuestion.answers.filter((a) => a !== null) as typeof currentQuestion.answers

      const result = (await submitAnswer(
        true,
        newState,
        pageLocations
      )) as AxiosResponse<unknown, unknown>

      // set loading state
      setIsLoadingSave(false)

      const { status } = result
      if (status >= 200 && status < 300) {
        savedEntries[index] = true
        setSaveResultText('Entry saved!')
        return true
      } else if (status >= 400 && status < 500) {
        setSaveResultText('Sorry! There was a client side error when trying to save. Please try again.')
      } else if (status >= 500) {
        setSaveResultText('Sorry! There was a server side error when trying to save. Please try again.')
      }
    }
    // set loading state if there are missing fields
    setIsLoadingSave(false)

    return missingFields.map((f) => f.name)
  }

  useEffect(() => {
    if ((saveResultText !== '' && saveResultText === 'Entry saved!') && isLoadingSave === false) {
      const timer = setTimeout(() => {
        setSaveResultText(''); // Ensure the state is properly reset after the delay
      }, 1500); // 1.5-second delay

      return () => clearTimeout(timer); // Cleanup the timer to avoid memory leaks
    }
  }, [isLoadingSave]);

  const deleteEntry = async (index: number) => {
    const updatedEntries = [...entriesData]
    updatedEntries.splice(index, 1)

    setEntriesData(updatedEntries)

    const newState = { ...stepData } as IQuestionnaireData
    const currentSegment = getCurrentSegmentData(
      pageLocations,
      newState
    ) as IQuestionSegmentData
    const currentQuestion = getCurrentQuestionData(
      pageLocations,
      newState
    ) as TFormQuestionData

    if (currentQuestion) {
      const { questionType } = currentQuestion
      switch (questionType) {
        case 'contractorForm':
          currentQuestion.answers = updatedEntries as IContractorFormData[]
          break
        case 'departmentHeadForm':
          currentQuestion.answers = updatedEntries as IDepartmentHeadFormData[]
          break
        case 'basicTeamForm':
          currentQuestion.answers = updatedEntries as IBasicTeamFormData[]
          break
        case 'nonOwnerForm':
          currentQuestion.answers = updatedEntries as INonOwnerFormData[]
          break
        case 'ownerForm':
          currentQuestion.answers = updatedEntries as IOwnerFormData[]
          break
        case 'leaderForm':
          currentQuestion.answers = updatedEntries as ILeaderFormData[]
          break
        // Team Member
        default:
          currentQuestion.answers = updatedEntries as ITeamMemberFormData[]
      }
    }

    newState.segments[pageLocations['segment']] = currentSegment

    const result = await submitAnswer(true, newState, pageLocations)

    if (!!result && result.status === 200) {
      return true
    }
  }

  /**
   * Add team member function
   * Creates a new entry and adds it to local state
   */
  const addEntry = () => {
    const newEntries: TFormAnswerData[] = [
      ...entriesData,
      generateFormDefaults(questionType)
    ]
    setEntriesData(newEntries)
  }

  useEffect(() => {
    setEntriesData(answers)
  }, [answers])

  const headerTitles = {
    teamMember: 'Team Members',
    ownerForm: 'Owners',
    contractorForm: '1099 Contractors',
    nonOwnerForm: 'Client Facing Non-Owners',
    leaderForm: 'Leaders',
    departmentHeadForm: 'Department Heads',
    basicTeamForm: 'Team Member' //default case if more explicit name isn't saved in triggerQuestionType
  }

  /**
   * Handles selection of correct header title; BasicTeamForm is used as a generic form type
   * and selects more specific name stored in triggerQuestionType
   */
  const selectHeaderTitle = (questionType: TFormQuestionTypes) => {
    if (questionType == 'basicTeamForm' && triggerQuestionType) {
      return capitalizeAndSplit(triggerQuestionType)
    }
    return headerTitles[questionType] || 'Default Header Title'
  }
  const headerTitle = selectHeaderTitle(questionType)

  // Handle close button click for saving error dialog
  const handleCloseErrorDialog = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setIsLoadingSave(false);
    setSaveResultText(''); // Reset the save result text
  }

  return (
    <Card
      className={cx('c-flow u-p-500', className)}
      bgColorClass={tw`u-bg-primary-300`}
    >
      <Accordion.Main className='c-flow' multipleOpen>
        <CollapsibleItemsCard.Header
          title={headerTitle}
          numOfItems={entriesData.length}
        />
        <div className='c-flow' ref={questionRef}>
          {/* Loading overlay for team edit */}
          {/* This will show a loading spinner when isLoadingSave is true OR when the save result is displaying*/}
          {(isLoadingSave === true || saveResultText !== '') && (
            <div
              // Create a full-screen overlay with a semi-transparent background
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 9999,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
              }}
            >
              {saveResultText !== '' ? (
                saveResultText === 'Entry saved!' ? (
                  <div
                    style={{
                      backgroundColor: 'white',
                      padding: '1.5rem',
                      borderRadius: '0.375rem',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                      width: '350px', // Set the width of the white background div
                    }}
                  >
                    <p
                      style={{
                        fontSize: '1.875rem', // equivalent to text-3xl
                        fontWeight: 'bold',
                        textAlign: 'center',
                      }}
                      className='u-text-state-good'
                    >
                      {saveResultText}
                    </p>
                  </div>
                ) : (
                  <div
                    style={{
                      backgroundColor: 'white',
                      padding: '1.5rem',
                      borderRadius: '0.375rem',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                      width: '450px', // Set the width of the white background div
                      textAlign: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <p
                      style={{
                        fontSize: '1.875rem', // equivalent to text-3xl
                        fontWeight: 'bold',
                        textAlign: 'center',
                        marginBottom: '1rem',
                      }}
                      className='u-text-state-danger'
                    >
                      {saveResultText}
                    </p>
                    <StyledButton
                      type='a'
                      href='#'
                      theme='secondary'
                      themeStyle='ghost'
                      className='u-flex u-justify-center u-whitespace-nowrap'
                      style={{
                        width: '100px', // Set the button width
                      }}
                      onClick={(e) => handleCloseErrorDialog(e)}
                    >
                      Close
                    </StyledButton>
                  </div>
                )
              ) : (
                <LoadingSpinner />
              )}
            </div>
          )}
          {entriesData &&
            entriesData.map((entry, i) =>
              questionType === 'teamMember' ? (
                <TeamMemberForm
                  data={entry as ITeamMemberFormData}
                  entriesTitle={entriesTitle}
                  index={i}
                  key={`TeamMember_${i}`}
                  deleteEntry={deleteEntry}
                  saveEntry={saveEntry}
                  saved={savedEntries[i]}
                />
              ) : questionType === 'ownerForm' ? (
                <OwnerForm
                  data={entry as IOwnerFormData}
                  entriesTitle={entriesTitle}
                  index={i}
                  key={`OwnerForm_${i}`}
                  info={
                    (data as IOwnerQuestionData).additionalNotesTooltip ||
                    undefined
                  }
                  deleteEntry={deleteEntry}
                  saveEntry={saveEntry}
                  saved={savedEntries[i]}
                />
              ) : questionType === 'nonOwnerForm' ? (
                <NonOwnerForm
                  data={entry as INonOwnerFormData}
                  entriesTitle={entriesTitle}
                  index={i}
                  key={`NonOwnerForm_${i}`}
                  deleteEntry={deleteEntry}
                  saveEntry={saveEntry}
                  saved={savedEntries[i]}
                />
              ) : questionType === 'leaderForm' ? (
                <LeaderForm
                  data={entry as ILeaderFormData}
                  entriesTitle={entriesTitle}
                  index={i}
                  key={`LeaderForm_${i}`}
                  deleteEntry={deleteEntry}
                  saveEntry={saveEntry}
                  saved={savedEntries[i]}
                />
              ) : questionType === 'departmentHeadForm' ? (
                <DepartmentHeadForm
                  data={entry as IDepartmentHeadFormData}
                  entriesTitle={entriesTitle}
                  index={i}
                  key={`DepartmentHeadForm_${i}`}
                  deleteEntry={deleteEntry}
                  saveEntry={saveEntry}
                  saved={savedEntries[i]}
                />
              ) : questionType === 'basicTeamForm' ? (
                <BasicTeamForm
                  data={entry as IBasicTeamFormData}
                  entriesTitle={entriesTitle}
                  index={i}
                  key={`BasicTeamForm_${i}`}
                  deleteEntry={deleteEntry}
                  saveEntry={saveEntry}
                  saved={savedEntries[i]}
                />
              )
                : (
                  <ContractorForm
                    data={entry as IContractorFormData}
                    entriesTitle={entriesTitle}
                    index={i}
                    key={`TeamMember_${i}`}
                    deleteEntry={deleteEntry}
                    saveEntry={saveEntry}
                    saved={savedEntries[i]}
                  />
                )
            )}
        </div>
      </Accordion.Main>
      <div className='u-flex u-justify-center'>
        <StyledButton
          type='button'
          theme='darkAlt'
          themeStyle='outline'
          onClick={addEntry}
        >
          Add Team Member
        </StyledButton>
      </div>
    </Card>
  )
}

export default TeamMembersQuestion
